import { useEffect, useMemo, useRef, useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import { usePageSeo, SITE_ORIGIN } from "@/hooks/usePageSeo";
import { chatRooms, chatMessages, quickReplies, onlineNames, type ChatMessage } from "@/mocks/chat";

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: "聊天室 - 诱惑吃瓜",
      url: `${SITE_ORIGIN}/chat`,
      inLanguage: "zh-CN",
      description: "进入诱惑吃瓜聊天室，与同好一起蹲后续、聊热点、整理时间线。",
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
        { "@type": "ListItem", position: 2, name: "聊天室" },
      ],
    },
  ],
};

const seedByRoom: Record<string, ChatMessage[]> = chatRooms.reduce((acc, room) => {
  acc[room.id] = chatMessages.map((msg) => ({ ...msg }));
  return acc;
}, {} as Record<string, ChatMessage[]>);

const formatTime = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

export default function Chat() {
  const [activeRoom, setActiveRoom] = useState(chatRooms[0].id);
  const [threads, setThreads] = useState<Record<string, ChatMessage[]>>(seedByRoom);
  const [input, setInput] = useState("");
  const [showRooms, setShowRooms] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  usePageSeo({
    title: "聊天室 - 边吃边聊蹲后续 | 诱惑吃瓜",
    description:
      "诱惑吃瓜聊天室聚集最会吃瓜的一群人，主瓜田、综艺蹲守屋、反转档案馆等房间随便逛，最新进展第一时间同步，边吃边聊根本停不下来。",
    keywords: "吃瓜聊天室,群聊蹲后续,娱乐讨论,综艺蹲守,反转档案馆",
    path: "/chat",
    jsonLd,
  });

  const room = useMemo(() => chatRooms.find((r) => r.id === activeRoom) ?? chatRooms[0], [activeRoom]);
  const messages = threads[activeRoom] ?? [];

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, activeRoom]);

  const send = () => {
    const text = input.trim();
    if (!text) return;
    const msg: ChatMessage = {
      id: Date.now(),
      user: "我",
      avatar: "ri-user-smile-line",
      text,
      time: formatTime(),
      tag: "本人",
    };
    setThreads((prev) => ({ ...prev, [activeRoom]: [...(prev[activeRoom] ?? []), msg] }));
    setInput("");
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />
      <main className="flex-1 pt-16 md:pt-20">
        <section className="w-full px-4 md:px-10 pt-8 pb-8 md:pt-10 md:pb-10 bg-background-100/60">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "聊天室" }]} />
          <div className="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div className="max-w-2xl">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-950">聊天室：一起蹲后续</h1>
              <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                选一个房间进去，和大家边吃边聊。有人抛料、有人考据，也有人专门整理时间线，最新进展第一时间同步。
              </p>
            </div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-600 w-fit">
              <span className="w-2 h-2 rounded-full bg-primary-500 animate-pulseSoft"></span>
              全站 {chatRooms.reduce((sum, r) => sum + r.online, 0).toLocaleString()} 人在线
            </span>
          </div>
        </section>

        <section className="w-full px-4 md:px-10 py-8 md:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            <aside className="lg:col-span-3">
              <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowRooms((v) => !v)}
                  aria-expanded={showRooms}
                  className="lg:cursor-default w-full flex items-center justify-between px-5 py-4"
                >
                  <span className="text-sm font-semibold text-foreground-950 flex items-center gap-2">
                    <i className="ri-layout-grid-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
                    聊天室房间
                  </span>
                  <i
                    className={`${showRooms ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} lg:hidden w-5 h-5 flex items-center justify-center text-foreground-500`}
                  ></i>
                </button>
                <nav className={`${showRooms ? "block" : "hidden"} lg:block px-2 pb-3`} aria-label="聊天室房间列表">
                  {chatRooms.map((item) => {
                    const isActive = item.id === activeRoom;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          setActiveRoom(item.id);
                          setShowRooms(false);
                        }}
                        className={`w-full flex items-start gap-3 px-3 py-3 rounded-md text-left transition-colors ${
                          isActive ? "bg-primary-50 border border-primary-200" : "hover:bg-background-100"
                        }`}
                      >
                        <span
                          className={`w-9 h-9 shrink-0 rounded-md flex items-center justify-center text-base ${
                            isActive ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600"
                          }`}
                        >
                          <i className={`${item.icon} w-5 h-5 flex items-center justify-center`}></i>
                        </span>
                        <span className="min-w-0">
                          <span className="flex items-center gap-1.5">
                            <span className={`text-sm ${isActive ? "font-medium text-primary-700" : "text-foreground-900"}`}>
                              {item.name}
                            </span>
                            {item.hot ? (
                              <span className="px-1.5 py-0.5 rounded-full bg-accent-500 text-foreground-950 text-[10px]">
                                HOT
                              </span>
                            ) : null}
                          </span>
                          <span className="mt-1 block text-xs text-foreground-500 leading-snug">{item.desc}</span>
                          <span className="mt-1 block text-[11px] text-foreground-400">
                            {item.online.toLocaleString()} 人在线
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </aside>

            <div className="lg:col-span-6">
              <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden flex flex-col h-[560px] md:h-[640px]">
                <div className="px-5 py-4 border-b border-background-200 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <span className="w-9 h-9 rounded-md bg-primary-500 text-background-50 flex items-center justify-center">
                      <i className={`${room.icon} w-5 h-5 flex items-center justify-center`}></i>
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">{room.name}</p>
                      <p className="text-xs text-foreground-500">{room.desc}</p>
                    </div>
                  </div>
                  <span className="flex items-center gap-1.5 text-xs text-primary-600 shrink-0">
                    <span className="w-2 h-2 rounded-full bg-primary-500 animate-pulseSoft"></span>
                    {room.online.toLocaleString()} 在线
                  </span>
                </div>

                <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-background-50">
                  <p className="text-center text-xs text-foreground-400 py-1">— 已进入「{room.name}」 —</p>
                  {messages.map((msg) => {
                    const isMe = msg.user === "我";
                    return (
                      <div key={msg.id} className={`flex items-start gap-3 ${isMe ? "flex-row-reverse" : ""}`}>
                        <span
                          className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-base ${
                            isMe ? "bg-primary-100 text-primary-700" : "bg-accent-100 text-accent-700"
                          }`}
                        >
                          <i className={`${msg.avatar} w-5 h-5 flex items-center justify-center`}></i>
                        </span>
                        <div className={`min-w-0 max-w-[80%] ${isMe ? "text-right" : ""}`}>
                          <div className={`flex items-center gap-2 text-xs ${isMe ? "justify-end" : ""}`}>
                            <span className="font-medium text-foreground-900">{msg.user}</span>
                            {msg.tag ? (
                              <span className="px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-[10px]">
                                {msg.tag}
                              </span>
                            ) : null}
                            <span className="text-foreground-400">{msg.time}</span>
                          </div>
                          <p
                            className={`mt-1.5 inline-block text-sm leading-relaxed rounded-lg px-3.5 py-2.5 ${
                              isMe
                                ? "bg-primary-500 text-background-50"
                                : "bg-background-100 text-foreground-700"
                            }`}
                          >
                            {msg.text}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="border-t border-background-200 p-4 bg-background-50">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {quickReplies.map((reply) => (
                      <button
                        key={reply}
                        type="button"
                        onClick={() => setInput(reply)}
                        className="px-3 py-1.5 rounded-full border border-background-200 text-xs text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
                      >
                        {reply}
                      </button>
                    ))}
                  </div>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      send();
                    }}
                    className="flex items-center gap-2"
                  >
                    <div className="flex-1 flex items-center gap-2 h-11 px-3.5 rounded-md bg-background-100 border border-background-200 focus-within:border-primary-400 transition-colors">
                      <i className="ri-edit-2-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
                      <input
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        maxLength={120}
                        placeholder="说点什么，和大家一起蹲后续…"
                        aria-label="输入消息"
                        className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                      />
                      <span className="text-[11px] text-foreground-400 tabular-nums">{input.length}/120</span>
                    </div>
                    <button
                      type="submit"
                      disabled={input.trim() === ""}
                      className="h-11 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 whitespace-nowrap"
                    >
                      <i className="ri-send-plane-2-line w-4 h-4 flex items-center justify-center"></i>
                    </button>
                  </form>
                </div>
              </div>
            </div>

            <aside className="lg:col-span-3">
              <div className="flex flex-col gap-5">
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="text-sm font-semibold text-foreground-950 flex items-center gap-2">
                    <i className="ri-group-line w-4 h-4 flex items-center justify-center text-secondary-600"></i>
                    在线同好
                  </h2>
                  <ul className="mt-4 space-y-3">
                    {onlineNames.map((name, index) => (
                      <li key={name} className="flex items-center gap-3">
                        <span className="relative w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-600">
                          <i className="ri-user-3-line w-4 h-4 flex items-center justify-center"></i>
                          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-accent-500 border-2 border-background-50"></span>
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm text-foreground-900 truncate">{name}</span>
                          <span className="block text-xs text-foreground-500">
                            {index === 0 ? "楼长 · 在线" : "正在围观"}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-lg bg-secondary-950 p-5">
                  <h2 className="text-sm font-semibold text-background-50 flex items-center gap-2">
                    <i className="ri-lightbulb-flash-line w-4 h-4 flex items-center justify-center text-accent-400"></i>
                    围观小贴士
                  </h2>
                  <ul className="mt-3 space-y-2.5 text-xs text-secondary-200 leading-relaxed">
                    <li>· 先说事实，再说看法，争执会少一半</li>
                    <li>· 不搬运未经确认的截图</li>
                    <li>· 允许别人中途离场，不追着问立场</li>
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}